import React, { useState, useEffect, useRef } from 'react';
import '../style.css';

const API = 'https://culturlingua.com/blog_api.php';
const NAV_H = 72; // px — matches --header-height in style.css

const CATEGORY_COLORS = {
  'Inglés': '#3b6fe0', 'Consejos': '#059669', 'Certificaciones': '#7c3aed',
  'Para padres': '#ea580c', 'Empresas': '#0891b2', 'Español': '#dc2626', 'General': '#6b7280'
};

const EMOJI_GROUPS = [
  { label: 'Idiomas',       emojis: ['🌍','🗣️','💬','🗺️','🌐','📢','🔤','🔡','✏️','🖊️'] },
  { label: 'Aprendizaje',   emojis: ['📚','📖','🎓','🏫','🧠','💡','🔬','📝','📋','✅'] },
  { label: 'Inglés',        emojis: ['🇬🇧','🇺🇸','🎧','🎤','🎙️','📻','🗞️','📰','🧩','🎯'] },
  { label: 'Español',       emojis: ['🇲🇽','🇪🇸','🌮','💃','🎸','🌺','☀️','🏖️','🎭','🌻'] },
  { label: 'Comunicación',  emojis: ['💌','📧','📞','🤝','👥','🧑‍💼','💼','🏆','⭐','🎖️'] },
  { label: 'Tecnología',    emojis: ['💻','📱','🖥️','⌨️','🖱️','🎮','🤖','📡','🔧','⚙️'] },
  { label: 'Viajes',        emojis: ['✈️','🧳','🗼','🏛️','🗽','🏯','⛺','🚢','🌄','🌇'] },
  { label: 'Varios',        emojis: ['🔥','💪','🚀','🌟','💎','🎁','🎉','🎊','🌈','❤️'] },
];

function getToken() { return localStorage.getItem('blog_token'); }
function authHeaders() { return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getToken() }; }

// ── Shared mini-components ──────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '20px', borderTop: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900, color: 'var(--navy)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '6px' }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', marginBottom: '16px' }}>{children}</div>;
}

function Panel({ children, style }) {
  return <div style={{ background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '24px', ...style }}>{children}</div>;
}

// ── Emoji Picker ────────────────────────────────────────────────
function EmojiPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const allEmojis = EMOJI_GROUPS.flatMap(g => g.emojis);
  const filtered = filter ? allEmojis.filter(e => e.includes(filter)) : null;
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ width: '56px', height: '56px', borderRadius: '12px', border: `1.5px solid ${open ? '#1a1744' : '#e5e7eb'}`, background: 'white', fontSize: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value || '＋'}
        </button>
        {value && <button type="button" onClick={() => onChange('')} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '0.8rem' }}>Quitar</button>}
        <span style={{ color: '#999', fontSize: '0.8rem' }}>{value ? 'Emoji seleccionado' : 'Sin emoji — se usará color + iniciales'}</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '64px', left: 0, zIndex: 100, background: 'white', borderRadius: '16px', boxShadow: '0 8px 40px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', width: '360px', padding: '16px' }}>
          <input autoFocus placeholder="Buscar emoji…" value={filter} onChange={e => setFilter(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }} />
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {(filtered ? [{ label: 'Resultados', emojis: filtered }] : EMOJI_GROUPS).map(group => (
              <div key={group.label} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: '#bbb', marginBottom: '6px' }}>{group.label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {group.emojis.map(e => (
                    <button key={e} type="button" onClick={() => { onChange(e); setOpen(false); setFilter(''); }}
                      style={{ width: '36px', height: '36px', border: 'none', borderRadius: '8px', background: value === e ? '#f0f0ff' : 'transparent', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onMouseEnter={ev => ev.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={ev => ev.currentTarget.style.background = value === e ? '#f0f0ff' : 'transparent'}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Login ───────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [err,  setErr]  = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true); setErr('');
    const r = await fetch(`${API}?action=login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user, pass }) });
    const d = await r.json();
    setLoading(false);
    if (d.token) { localStorage.setItem('blog_token', d.token); onLogin(); }
    else setErr('Usuario o contraseña incorrectos');
  };
  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '20px', padding: '48px', width: '360px', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <h2 style={{ fontFamily: 'Fraunces, serif', color: 'var(--navy)', marginBottom: '8px', fontSize: '1.6rem' }}>Blog Admin</h2>
        <p style={{ color: '#999', fontSize: '0.875rem', marginBottom: '32px' }}>Culturlingua</p>
        <input placeholder="Usuario" value={user} onChange={e => setUser(e.target.value)} style={inputStyle} />
        <input type="password" placeholder="Contraseña" value={pass} onChange={e => setPass(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()} style={{ ...inputStyle, marginTop: '12px' }} />
        {err && <p style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: '8px' }}>{err}</p>}
        <button onClick={submit} disabled={loading}
          style={{ width: '100%', padding: '13px', marginTop: '20px', borderRadius: '10px', background: 'var(--navy)', color: 'white', border: 'none', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', cursor: 'pointer' }}>
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e5e7eb', fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', display: 'block' };
const labelStyle = { display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, color: 'var(--navy)', fontSize: '0.85rem', marginBottom: '6px' };

// ── Editor ──────────────────────────────────────────────────────
function Editor({ post, onSave, onCancel }) {
  const isNew = !post?.id;
  const [form, setForm] = useState({
    slug: post?.slug || '', title: post?.title || '', excerpt: post?.excerpt || '',
    content: post?.content || '', category: post?.category || 'Consejos',
    category_color: post?.category_color || '#3b6fe0', icon: post?.icon || '',
    read_time: post?.read_time || '4 min', published: post?.published ?? 1
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const autoSlug = (t) => t.toLowerCase()
    .replace(/[áàä]/g,'a').replace(/[éèë]/g,'e').replace(/[íìï]/g,'i').replace(/[óòö]/g,'o').replace(/[úùü]/g,'u').replace(/ñ/g,'n')
    .replace(/[^a-z0-9\s]/g,'').replace(/\s+/g,'-').slice(0,80);
  const save = async () => {
    setSaving(true); setErr('');
    const url = isNew ? `${API}?action=create` : `${API}?action=update&id=${post.id}`;
    const r = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(form) });
    const d = await r.json();
    setSaving(false);
    if (d.success) onSave(); else setErr(d.error || 'Error al guardar');
  };
  const previewColor = form.category_color || '#3b6fe0';
  return (
    <div style={{ maxWidth: '920px', margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <h2 style={{ fontFamily: 'Fraunces, serif', color: 'var(--navy)', fontSize: '1.5rem' }}>{isNew ? 'Nuevo artículo' : 'Editar artículo'}</h2>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '0.9rem' }}>← Volver</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '32px', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Título</label>
            <input value={form.title} style={inputStyle} onChange={e => { set('title', e.target.value); if (isNew) set('slug', autoSlug(e.target.value)); }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Slug (URL)</label>
              <input value={form.slug} style={inputStyle} onChange={e => set('slug', e.target.value)} />
              <p style={{ color: '#999', fontSize: '0.75rem', marginTop: '4px' }}>/blog/<strong>{form.slug || 'tu-slug-aqui'}</strong></p>
            </div>
            <div>
              <label style={labelStyle}>Tiempo de lectura</label>
              <input value={form.read_time} style={inputStyle} onChange={e => set('read_time', e.target.value)} placeholder="4 min" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Categoría</label>
              <select value={form.category} style={inputStyle}
                onChange={e => { set('category', e.target.value); set('category_color', CATEGORY_COLORS[e.target.value] || '#3b6fe0'); }}>
                {Object.keys(CATEGORY_COLORS).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Color categoría</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={form.category_color} onChange={e => set('category_color', e.target.value)}
                  style={{ width: '48px', height: '40px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', padding: '2px' }} />
                <input value={form.category_color} style={{ ...inputStyle, flex: 1 }} onChange={e => set('category_color', e.target.value)} />
              </div>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Icono del artículo</label>
            <EmojiPicker value={form.icon} onChange={v => set('icon', v)} />
          </div>
          <div>
            <label style={labelStyle}>Descripción corta (aparece en Google y en la lista)</label>
            <textarea value={form.excerpt} style={{ ...inputStyle, height: '80px', resize: 'vertical' }} onChange={e => set('excerpt', e.target.value)} />
            <p style={{ color: form.excerpt.length > 160 ? '#dc2626' : '#999', fontSize: '0.75rem', marginTop: '4px' }}>{form.excerpt.length}/160 caracteres</p>
          </div>
          <div>
            <label style={labelStyle}>Contenido HTML</label>
            <p style={{ color: '#999', fontSize: '0.75rem', marginBottom: '8px' }}>Usa: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;, &lt;ol&gt;&lt;li&gt;, &lt;strong&gt;, &lt;blockquote&gt;, &lt;a href=""&gt;</p>
            <textarea value={form.content} style={{ ...inputStyle, height: '400px', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.82rem' }} onChange={e => set('content', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.published === 1} onChange={e => set('published', e.target.checked ? 1 : 0)} style={{ width: '16px', height: '16px' }} />
              <span style={{ fontFamily: 'DM Sans', fontWeight: 600, color: 'var(--navy)' }}>Publicado</span>
            </label>
            <span style={{ color: '#999', fontSize: '0.82rem' }}>{form.published ? 'Visible en el blog público' : 'Borrador — no visible aún'}</span>
          </div>
          {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', color: '#dc2626', fontSize: '0.875rem' }}>{err}</div>}
          <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '13px 32px', borderRadius: '10px', background: 'var(--navy)', color: 'white', border: 'none', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', fontSize: '0.95rem' }}>
              {saving ? 'Guardando...' : (isNew ? 'Publicar artículo' : 'Guardar cambios')}
            </button>
            <button onClick={onCancel} style={{ padding: '13px 24px', borderRadius: '10px', background: '#f3f4f6', color: '#666', border: 'none', fontWeight: 600, fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
        {/* Live preview */}
        <div style={{ position: 'sticky', top: NAV_H + 16 }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: '#bbb', marginBottom: '12px' }}>Vista previa</div>
          <div style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(26,23,68,.10)' }}>
            <div style={{ height: '140px', background: `linear-gradient(135deg, ${previewColor}, ${previewColor}99)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position:'absolute',inset:0, backgroundImage:`radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)`, backgroundSize:'20px 20px' }}/>
              {form.icon
                ? <span style={{ fontSize: '52px', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.2))', position: 'relative', zIndex: 2 }}>{form.icon}</span>
                : <span style={{ fontFamily: 'Fraunces, serif', fontSize: '3.5rem', fontWeight: 900, color: 'rgba(255,255,255,0.2)', position: 'relative', zIndex: 2 }}>{(form.category||'Ge').substring(0,2).toUpperCase()}</span>
              }
            </div>
            <div style={{ padding: '18px 20px 16px' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', padding: '3px 9px', borderRadius: '100px', background: previewColor+'18', color: previewColor, marginBottom: '10px', display: 'inline-block' }}>{form.category||'Categoría'}</span>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1rem', fontWeight: 800, color: '#1a1744', lineHeight: 1.3, marginBottom: '8px' }}>{form.title||'Título del artículo'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #f0ede8', fontSize: '0.76rem' }}>
                <span style={{ color: '#c0b8a8' }}>Hoy</span>
                <span style={{ color: '#3b6fe0', fontWeight: 700 }}>{form.read_time||'4 min'} →</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Analytics tab ───────────────────────────────────────────────
function AnalyticsTab({ stats }) {
  if (!stats) return <p style={{ color: '#999', textAlign: 'center', padding: '40px' }}>Cargando estadísticas...</p>;

  const eng     = stats.engagement || {};
  const total   = stats.total || 0;
  const sessions = eng.total_sessions || 0;
  const bounced  = eng.total_bounced  || 0;
  const bounceRate = sessions > 0 ? Math.round((bounced / sessions) * 100) : 0;
  const ctrRate    = sessions > 0 ? ((eng.total_cta_clicks || 0) / sessions * 100).toFixed(1) : '0';

  // Device totals
  const devTotal = (eng.mobile_count||0) + (eng.tablet_count||0) + (eng.desktop_count||0) || 1;
  const devPct   = { mobile: Math.round((eng.mobile_count||0)/devTotal*100), tablet: Math.round((eng.tablet_count||0)/devTotal*100), desktop: Math.round((eng.desktop_count||0)/devTotal*100) };

  // Scroll buckets
  const sb = stats.scroll_buckets || {};
  const sbTotal = (parseInt(sb.s0)||0)+(parseInt(sb.s25)||0)+(parseInt(sb.s50)||0)+(parseInt(sb.s75)||0)+(parseInt(sb.s100)||0) || 1;
  const sbPct = (v) => Math.round((parseInt(v)||0)/sbTotal*100);

  // Source donut
  const org = stats.organic_total || 0;
  const int = stats.internal_total || 0;
  const srcTotal = org + int || 1;
  const orgPct = Math.round((org/srcTotal)*100);
  const R = 34, C = 2*Math.PI*R;
  const orgDash = (orgPct/100)*C;

  // Hourly heatmap — fill 0-23
  const hourMap = {};
  (stats.hourly||[]).forEach(r => { hourMap[r.hour_of_day] = parseInt(r.cnt)||0; });
  const maxHour = Math.max(1, ...Object.values(hourMap));
  const DAY_NAMES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
  const dayMap = {};
  (stats.daily||[]).forEach(r => { dayMap[r.day_of_week] = parseInt(r.cnt)||0; });
  const maxDay = Math.max(1, ...Object.values(dayMap));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Row 1 — core metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
        <StatCard label="Vistas totales"   value={(total||0).toLocaleString()}                      sub="todos los posts" accent="#3b6fe0"/>
        <StatCard label="Sesiones únicas"  value={(sessions||0).toLocaleString()}                   sub="con datos de engagement" accent="#059669"/>
        <StatCard label="CTA click-rate"   value={`${ctrRate}%`}                                    sub={`${eng.total_cta_clicks||0} clicks totales`} accent="#f5c842"/>
        <StatCard label="Tasa de rebote"   value={`${bounceRate}%`}                                 sub="&lt;10s y &lt;10% scroll" accent="#dc2626"/>
      </div>

      {/* Row 2 — engagement averages */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
        <StatCard label="Scroll promedio"  value={`${Math.round(eng.avg_scroll||0)}%`}             sub="profundidad de lectura"/>
        <StatCard label="Tiempo activo"    value={`${Math.round((eng.avg_time||0)/60*10)/10} min`} sub="promedio por sesión"/>
        <StatCard label="Posts +100 vistas" value={stats.over100||0}                               sub="muestran contador público"/>
      </div>

      {/* Row 3 — fuente de tráfico + dispositivo */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>

        {/* Fuente de tráfico */}
        <Panel>
          <SectionTitle>Fuente de tráfico</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <svg width="88" height="88" viewBox="0 0 88 88">
                <circle cx="44" cy="44" r={R} fill="none" stroke="#f0ece4" strokeWidth="11"/>
                <circle cx="44" cy="44" r={R} fill="none" stroke="#3b6fe0" strokeWidth="11"
                  strokeDasharray={`${orgDash} ${C-orgDash}`} strokeDashoffset={C*0.25} strokeLinecap="round"/>
                <circle cx="44" cy="44" r={R} fill="none" stroke="#f5c842" strokeWidth="11"
                  strokeDasharray={`${Math.max(0,C-orgDash-2)} ${orgDash+2}`} strokeDashoffset={C*0.25-orgDash-1} strokeLinecap="round"/>
              </svg>
              <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontFamily:'Fraunces,serif', fontSize:'1rem', fontWeight:900, color:'var(--navy)', lineHeight:1 }}>{orgPct}%</span>
                <span style={{ fontSize:'0.58rem', color:'#aaa', fontWeight:700 }}>orgánico</span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {[['#3b6fe0','🔍 Orgánico / directo', org], ['#f5c842','🧭 Navegación interna', int]].map(([c,l,v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    <span style={{ width:'8px', height:'8px', borderRadius:'2px', background:c, display:'inline-block' }}/>
                    <span style={{ fontSize:'0.8rem', color:'#555' }}>{l}</span>
                  </div>
                  <span style={{ fontFamily:'Fraunces,serif', fontWeight:900, color:'var(--navy)', fontSize:'1rem' }}>{v.toLocaleString()}</span>
                </div>
              ))}
              {(org+int)>0 && (
                <div style={{ marginTop:'8px', background: orgPct>=60?'#f0fdf4':'#fff7ed', borderRadius:'8px', padding:'8px 12px', fontSize:'0.75rem', color: orgPct>=60?'#166534':'#9a3412', fontWeight:500 }}>
                  {orgPct>=60 ? `${orgPct}% orgánico — el SEO está jalando bien.` : `Solo ${orgPct}% orgánico — refuerza el SEO.`}
                </div>
              )}
            </div>
          </div>
        </Panel>

        {/* Dispositivo */}
        <Panel>
          <SectionTitle>Dispositivo</SectionTitle>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px', marginTop:'4px' }}>
            {[['📱 Móvil', devPct.mobile, '#3b6fe0', eng.mobile_count||0],
              ['🖥️ Desktop', devPct.desktop, '#059669', eng.desktop_count||0],
              ['📐 Tablet', devPct.tablet, '#f5c842', eng.tablet_count||0]].map(([l,pct,c,n]) => (
              <div key={l}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                  <span style={{ fontSize:'0.82rem', color:'#555' }}>{l}</span>
                  <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--navy)' }}>{pct}% <span style={{ color:'#aaa', fontWeight:400 }}>({n})</span></span>
                </div>
                <div style={{ height:'6px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:c, borderRadius:'3px', transition:'width 0.4s' }}/>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Row 4 — scroll depth buckets */}
      <Panel>
        <SectionTitle>Profundidad de lectura — ¿hasta dónde leen?</SectionTitle>
        <div style={{ display:'flex', alignItems:'flex-end', gap:'8px', height:'80px' }}>
          {[['0–24%','s0','#fecaca'],['25–49%','s25','#fed7aa'],['50–74%','s50','#fde68a'],['75–99%','s75','#a7f3d0'],['100%','s100','#6ee7b7']].map(([lbl,key,color]) => {
            const pct = sbPct(sb[key]);
            return (
              <div key={key} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px' }}>
                <span style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--navy)' }}>{pct}%</span>
                <div style={{ width:'100%', height:`${Math.max(4,pct)}%`, background:color, borderRadius:'4px 4px 0 0', transition:'height 0.5s', minHeight:'4px' }}/>
                <span style={{ fontSize:'0.68rem', color:'#aaa', textAlign:'center' }}>{lbl}</span>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop:'12px', fontSize:'0.75rem', color:'#888' }}>
          {sbPct(sb.s100) >= 30
            ? `✅ ${sbPct(sb.s100)}% de lectores llegan al final — buen contenido.`
            : sbPct(sb.s0) >= 50
              ? `⚠️ ${sbPct(sb.s0)}% se van en la primera cuarta parte — revisa el intro y el título.`
              : `📊 ${sbPct(sb.s50)}% pasa de la mitad del artículo.`}
        </div>
      </Panel>

      {/* Row 5 — UTM sources */}
      {(stats.utm_sources||[]).length > 0 && (
        <Panel>
          <SectionTitle>Fuentes UTM identificadas</SectionTitle>
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            {(stats.utm_sources||[]).map((u,i) => {
              const maxUTM = stats.utm_sources[0]?.cnt || 1;
              const pct = Math.round((u.cnt/maxUTM)*100);
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                  <div style={{ width:'120px', fontSize:'0.85rem', color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', flexShrink:0 }}>{u.utm_source}</div>
                  <div style={{ flex:1, height:'6px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:'#7c3aed', borderRadius:'3px' }}/>
                  </div>
                  <div style={{ fontSize:'0.85rem', fontWeight:700, color:'var(--navy)', width:'28px', textAlign:'right', flexShrink:0 }}>{u.cnt}</div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Row 6 — Horas del día */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'14px' }}>
        <Panel>
          <SectionTitle>Horas del día con más lecturas</SectionTitle>
          <div style={{ display:'flex', alignItems:'flex-end', gap:'2px', height:'60px', marginBottom:'6px' }}>
            {Array.from({length:24},(_,h)=>{
              const cnt = hourMap[h]||0;
              const pct = Math.round((cnt/maxHour)*100);
              const isAM = h < 12;
              return (
                <div key={h} title={`${h}:00 — ${cnt} visitas`}
                  style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'flex-end', height:'100%', cursor:'default' }}>
                  <div style={{ width:'100%', height:`${Math.max(2,pct)}%`, background: pct>=70?'#3b6fe0': pct>=30?'#93c5fd':'#dbeafe', borderRadius:'2px 2px 0 0', minHeight:'2px' }}/>
                </div>
              );
            })}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            <span style={{ fontSize:'0.65rem', color:'#aaa' }}>12am</span>
            <span style={{ fontSize:'0.65rem', color:'#aaa' }}>6am</span>
            <span style={{ fontSize:'0.65rem', color:'#aaa' }}>12pm</span>
            <span style={{ fontSize:'0.65rem', color:'#aaa' }}>6pm</span>
            <span style={{ fontSize:'0.65rem', color:'#aaa' }}>11pm</span>
          </div>
          {Object.keys(hourMap).length > 0 && (() => {
            const peakH = Object.entries(hourMap).sort((a,b)=>b[1]-a[1])[0];
            return <p style={{ fontSize:'0.75rem', color:'#888', marginTop:'8px' }}>Pico: {peakH[0]}:00 h con {peakH[1]} visitas</p>;
          })()}
        </Panel>

        <Panel>
          <SectionTitle>Día de la semana</SectionTitle>
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {DAY_NAMES.map((d,i) => {
              const cnt = dayMap[i]||0;
              const pct = Math.round((cnt/maxDay)*100);
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ width:'28px', fontSize:'0.75rem', color:'#888', flexShrink:0 }}>{d}</span>
                  <div style={{ flex:1, height:'5px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background: i===0||i===6 ? '#f5c842':'#3b6fe0', borderRadius:'3px' }}/>
                  </div>
                  <span style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--navy)', width:'24px', textAlign:'right', flexShrink:0 }}>{cnt}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* Row 7 — Posts con engagement detail */}
      <Panel>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <SectionTitle>Rendimiento por artículo</SectionTitle>
          <div style={{ display:'flex', gap:'16px' }}>
            {[['#3b6fe0','Orgánico'],['#f5c842','Interno']].map(([c,l])=>(
              <span key={l} style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'0.72rem', color:'#888', fontWeight:600 }}>
                <span style={{ width:'8px', height:'8px', borderRadius:'2px', background:c, display:'inline-block' }}/>{l}
              </span>
            ))}
          </div>
        </div>
        {(stats.posts||[]).length===0 && <p style={{ color:'#999', fontSize:'0.875rem' }}>Sin datos aún.</p>}
        {(stats.posts||[]).map((p,i)=>{
          const max = stats.posts[0]?.views || 1;
          const totPct = Math.round(((p.views||0)/max)*100);
          const orgPct2= Math.round(((p.views_organic||0)/max)*100);
          const intPct2= Math.round(((p.views_internal||0)/max)*100);
          const hasEng = p.avg_scroll != null;
          const hasSrc = (p.views_organic||0)+(p.views_internal||0)>0;
          return (
            <div key={i} style={{ marginBottom:'20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px' }}>
                <div style={{ width:'4px', height:'14px', background:p.category_color||'#3b6fe0', borderRadius:'2px', flexShrink:0 }}/>
                <div style={{ flex:1, fontSize:'0.85rem', color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.title}</div>
                <div style={{ display:'flex', gap:'10px', flexShrink:0, alignItems:'center' }}>
                  {hasEng && <>
                    <span style={{ fontSize:'0.7rem', color:'#888' }} title="Scroll promedio">📜 {p.avg_scroll}%</span>
                    <span style={{ fontSize:'0.7rem', color:'#888' }} title="Tiempo activo promedio">⏱ {Math.round((p.avg_time||0)/60*10)/10}m</span>
                    {(p.cta_clicks||0)>0 && <span style={{ fontSize:'0.7rem', color:'#059669', fontWeight:700 }} title="Clicks en CTA">🎯 {p.cta_clicks}</span>}
                  </>}
                  {hasSrc && <>
                    <span style={{ fontSize:'0.73rem', color:'#3b6fe0', fontWeight:700 }}>{p.views_organic||0}</span>
                    <span style={{ color:'#ddd', fontSize:'0.7rem' }}>|</span>
                    <span style={{ fontSize:'0.73rem', color:'#b8860b', fontWeight:700 }}>{p.views_internal||0}</span>
                    <span style={{ color:'#ddd', fontSize:'0.7rem' }}>|</span>
                  </>}
                  <span style={{ fontSize:'0.85rem', fontWeight:700, color:'var(--navy)', width:'32px', textAlign:'right' }}>{p.views}</span>
                </div>
              </div>
              <div style={{ height:'6px', background:'#f0ece4', borderRadius:'3px', overflow:'hidden', display:'flex' }}>
                {hasSrc
                  ? <><div style={{ width:`${orgPct2}%`, height:'100%', background:'#3b6fe0' }}/><div style={{ width:`${intPct2}%`, height:'100%', background:'#f5c842' }}/></>
                  : <div style={{ width:`${totPct}%`, height:'100%', background:p.category_color||'#3b6fe0', borderRadius:'3px' }}/>
                }
              </div>
              {hasEng && (p.bounced||0)>0 && (p.sessions||0)>0 && (
                <div style={{ fontSize:'0.68rem', color:'#aaa', marginTop:'3px' }}>
                  Rebote: {Math.round((p.bounced/p.sessions)*100)}% de {p.sessions} sesiones
                </div>
              )}
            </div>
          );
        })}
      </Panel>

      {/* Row 8 — Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
        <StatCard label="Top categoría" value={stats.top_cat?.category||'—'} sub={`${(stats.top_cat?.total||0).toLocaleString()} vistas acumuladas`}/>
        <StatCard label="Promedio por post" value={stats.avg||0} sub="vistas por artículo publicado"/>
      </div>
    </div>
  );
}

// ── Main Admin ──────────────────────────────────────────────────
export default function BlogAdmin() {
  const [authed,  setAuthed]  = useState(!!getToken());
  const [posts,   setPosts]   = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab,     setTab]     = useState('articulos');
  const [stats,   setStats]   = useState(null);

  const loadStats = () => {
    fetch(`${API}?action=stats`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setStats(d)).catch(()=>{});
  };
  const loadPosts = () => {
    setLoading(true);
    fetch(`${API}?action=admin_posts`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setPosts(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { if (authed) { loadPosts(); loadStats(); } }, [authed]);

  const editPost = async (id) => {
    const r = await fetch(`${API}?action=admin_post&id=${id}`, { headers: authHeaders() });
    setEditing(await r.json());
  };
  const deletePost = async (id, title) => {
    if (!window.confirm(`¿Eliminar "${title}"? Esta acción no se puede deshacer.`)) return;
    await fetch(`${API}?action=delete&id=${id}`, { method: 'DELETE', headers: authHeaders() });
    loadPosts();
  };
  const togglePublished = async (post) => {
    await fetch(`${API}?action=update&id=${post.id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ ...post, published: post.published ? 0 : 1 }) });
    loadPosts();
  };
  const logout = () => { localStorage.removeItem('blog_token'); setAuthed(false); };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  // ── Topbar shared ──
  const Topbar = ({ extra }) => (
    <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 32px', height: '52px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 700, color: 'var(--navy)', fontSize: '1rem' }}>Blog Admin · Culturlingua</span>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        {extra}
        <a href="/blog" target="_blank" style={{ color: '#999', fontSize: '0.85rem', textDecoration: 'none' }}>Ver blog →</a>
        <button onClick={logout}
          style={{ padding: '6px 14px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: 'white', color: '#666', fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600 }}>
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  if (editing !== null) return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingTop: NAV_H }}>
      <Topbar extra={<button onClick={() => setEditing(null)} style={{ background:'none', border:'none', color:'#999', cursor:'pointer', fontSize:'0.85rem' }}>← Volver</button>} />
      <Editor post={editing} onSave={() => { setEditing(null); loadPosts(); }} onCancel={() => setEditing(null)} />
    </div>
  );

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingTop: NAV_H }}>
      <Topbar />

      <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '32px 24px' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', borderBottom: '1.5px solid var(--border)', marginBottom: '28px' }}>
          {[['articulos','📝 Artículos'],['analytics','📊 Analytics']].map(([t,lbl]) => (
            <button key={t} onClick={() => { setTab(t); if (t==='analytics') loadStats(); }}
              style={{ padding: '10px 22px', borderRadius: '10px 10px 0 0', border: '1.5px solid', borderBottom: 'none',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', marginBottom: '-1.5px',
                background: tab===t ? 'white' : 'transparent',
                borderColor: tab===t ? 'var(--border)' : 'transparent',
                color: tab===t ? 'var(--navy)' : 'var(--muted)' }}>
              {lbl}
            </button>
          ))}
        </div>

        {/* ── Tab: Artículos ── */}
        {tab==='articulos' && (<>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'28px' }}>
            <div>
              <h1 style={{ fontFamily:'Fraunces, serif', color:'var(--navy)', fontSize:'1.8rem', marginBottom:'4px' }}>Artículos</h1>
              <p style={{ color:'#999', fontSize:'0.875rem' }}>{posts.length} artículo{posts.length!==1?'s':''} en total</p>
            </div>
            <button onClick={() => setEditing({})}
              style={{ padding:'12px 24px', borderRadius:'10px', background:'var(--navy)', color:'white', border:'none', fontWeight:700, fontFamily:'DM Sans, sans-serif', cursor:'pointer', fontSize:'0.9rem' }}>
              + Nuevo artículo
            </button>
          </div>

          {loading && <p style={{ color:'#999', textAlign:'center', padding:'40px' }}>Cargando...</p>}
          {!loading && posts.length===0 && (
            <div style={{ textAlign:'center', padding:'80px', background:'white', borderRadius:'16px' }}>
              <p style={{ fontSize:'2.5rem' }}>📝</p>
              <h3 style={{ fontFamily:'Fraunces, serif', color:'var(--navy)', marginTop:'12px' }}>Sin artículos aún</h3>
              <p style={{ color:'#999', marginTop:'8px' }}>Haz clic en "+ Nuevo artículo" para empezar.</p>
            </div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            {posts.map(post => (
              <div key={post.id} style={{ background:'white', borderRadius:'14px', padding:'16px 20px', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', display:'flex', alignItems:'center', gap:'14px' }}>
                <div style={{ width:'4px', height:'44px', background:post.category_color||'#3b6fe0', borderRadius:'2px', flexShrink:0 }}/>
                <div style={{ width:'38px', height:'38px', borderRadius:'9px', background:(post.category_color||'#3b6fe0')+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:post.icon?'20px':'13px', flexShrink:0, color:post.category_color||'#3b6fe0', fontWeight:800 }}>
                  {post.icon||(post.category||'G').substring(0,2).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                    <span style={{ fontFamily:'Fraunces, serif', fontWeight:700, color:'var(--navy)', fontSize:'0.97rem' }}>{post.title}</span>
                    <span style={{ fontSize:'0.68rem', padding:'2px 8px', borderRadius:'100px', fontWeight:700, background:post.published?'#d1fae5':'#f3f4f6', color:post.published?'#065f46':'#6b7280' }}>
                      {post.published?'Publicado':'Borrador'}
                    </span>
                  </div>
                  <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
                    <span style={{ color:'#999', fontSize:'0.76rem' }}>{post.category}</span>
                    <span style={{ color:'#ddd' }}>·</span>
                    <span style={{ color:'#999', fontSize:'0.76rem' }}>/blog/{post.slug}</span>
                    <span style={{ color:'#ddd' }}>·</span>
                    <span style={{ color:'#999', fontSize:'0.76rem' }}>{post.read_time}</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:'7px', flexShrink:0 }}>
                  <button onClick={() => togglePublished(post)}
                    style={{ padding:'6px 12px', borderRadius:'8px', border:'1.5px solid #e5e7eb', background:'white', color:'#666', fontFamily:'DM Sans, sans-serif', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 }}>
                    {post.published?'Ocultar':'Publicar'}
                  </button>
                  <button onClick={() => editPost(post.id)}
                    style={{ padding:'6px 14px', borderRadius:'8px', border:'none', background:'var(--navy)', color:'white', fontFamily:'DM Sans, sans-serif', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 }}>
                    Editar
                  </button>
                  <button onClick={() => deletePost(post.id, post.title)}
                    style={{ padding:'6px 12px', borderRadius:'8px', border:'1.5px solid #fecaca', background:'white', color:'#dc2626', fontFamily:'DM Sans, sans-serif', fontSize:'0.78rem', cursor:'pointer', fontWeight:600 }}>
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>)}

        {/* ── Tab: Analytics ── */}
        {tab==='analytics' && <AnalyticsTab stats={stats} />}

      </div>
    </div>
  );
}